import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loginTransportErrorMessage } from "../../api/authErrors";
import {
  loginWithPassword,
  loginWithRecovery,
  loginWithTotp,
  startLogin,
  startTotpEnrollment,
  verifyTotpEnrollment,
  type LoginNextStep,
} from "../../api/authApi";
import { useAuth } from "../../state/AuthProvider";
import { routeAfterLogin } from "../../auth/routeAfterLogin";
import type { AuthUser } from "../../auth/types";
import { AuthShell } from "./AuthShell";

type Step = "email" | LoginNextStep | "recovery" | "recovery_codes";

function AuthSubmitButton({
  label,
  loadingLabel,
  isSubmitting,
}: {
  label: string;
  loadingLabel: string;
  isSubmitting: boolean;
}) {
  return (
    <button className="primary-button auth-submit" type="submit" disabled={isSubmitting}>
      {isSubmitting ? (
        <span className="auth-submit__inner">
          <span className="auth-spinner" aria-hidden="true" />
          {loadingLabel}
        </span>
      ) : (
        label
      )}
    </button>
  );
}

export function LoginPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [manualKey, setManualKey] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [enrolledUser, setEnrolledUser] = useState<AuthUser | null>(null);
  const [savedRecoveryAck, setSavedRecoveryAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleEmailContinue(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await startLogin(email.trim());
      setChallengeId(result.challengeId);
      setEmail(result.email);
      if (result.nextStep === "totp_enroll") {
        const enroll = await startTotpEnrollment(result.challengeId);
        setQrDataUrl(enroll.qrDataUrl);
        setManualKey(enroll.manualKey);
      }
      setStep(result.nextStep);
    } catch (cause) {
      setError(loginTransportErrorMessage(cause));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const user = await loginWithPassword(challengeId, password);
      completeLogin(user);
      navigate(routeAfterLogin(user, location.state), { replace: true });
    } catch (cause) {
      setError(loginTransportErrorMessage(cause));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleTotpVerify(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const user = await loginWithTotp(challengeId, totpCode);
      completeLogin(user);
      navigate(routeAfterLogin(user, location.state), { replace: true });
    } catch {
      setError("Invalid authenticator code.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleTotpEnroll(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await verifyTotpEnrollment(challengeId, totpCode);
      setRecoveryCodes(result.recoveryCodes);
      completeLogin(result.user);
      setEnrolledUser(result.user);
      setStep("recovery_codes");
    } catch {
      setError("Invalid code. Check your authenticator and try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRecovery(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const user = await loginWithRecovery(challengeId, recoveryCode);
      completeLogin(user);
      navigate(routeAfterLogin(user, location.state), { replace: true });
    } catch {
      setError("Invalid recovery code.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function resetToEmail() {
    setStep("email");
    setPassword("");
    setTotpCode("");
    setRecoveryCode("");
    setQrDataUrl(null);
    setError(null);
  }

  const showEmailSubtitle = step !== "email" && step !== "recovery_codes";

  if (step === "email") {
    return (
      <AuthShell title="Sign in to continue">
        <form className="auth-form" onSubmit={(event) => void handleEmailContinue(event)}>
          <label className="field-label auth-field">
            Email
            <input
              className="field-input auth-input"
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <AuthSubmitButton label="Continue" loadingLabel="Continuing…" isSubmitting={isSubmitting} />
        </form>
      </AuthShell>
    );
  }

  if (step === "password") {
    return (
      <AuthShell title="Enter your password" subtitle={email}>
        <form className="auth-form" onSubmit={(event) => void handlePassword(event)}>
          <label className="field-label auth-field">
            Password
            <input
              className="field-input auth-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <AuthSubmitButton label="Sign in" loadingLabel="Signing in…" isSubmitting={isSubmitting} />
          <button type="button" className="text-button auth-link" onClick={resetToEmail}>
            Back to email
          </button>
        </form>
      </AuthShell>
    );
  }

  if (step === "totp_verify") {
    return (
      <AuthShell title="Enter authenticator code" subtitle={email}>
        <form className="auth-form" onSubmit={(event) => void handleTotpVerify(event)}>
          <label className="field-label auth-field">
            6-digit code
            <input
              className="field-input auth-input auth-totp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value)}
              required
            />
          </label>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <AuthSubmitButton label="Verify" loadingLabel="Verifying…" isSubmitting={isSubmitting} />
          <button type="button" className="text-button auth-link" onClick={() => setStep("recovery")}>
            Use recovery code
          </button>
          <button type="button" className="text-button auth-link" onClick={resetToEmail}>
            Back to email
          </button>
        </form>
      </AuthShell>
    );
  }

  if (step === "totp_enroll") {
    return (
      <AuthShell
        title="Set up authenticator"
        subtitle={email}
        lead="Scan this QR code with your authenticator app, then enter the 6-digit code it generates."
      >
        {qrDataUrl ? (
          <img className="auth-qr" src={qrDataUrl} alt="Authenticator QR code" width={220} height={220} />
        ) : null}
        <p className="auth-manual-key">
          Manual key: <code>{manualKey}</code>
        </p>
        <form className="auth-form" onSubmit={(event) => void handleTotpEnroll(event)}>
          <label className="field-label auth-field">
            Verification code
            <input
              className="field-input auth-input auth-totp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value)}
              required
            />
          </label>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <AuthSubmitButton label="Verify & continue" loadingLabel="Verifying…" isSubmitting={isSubmitting} />
        </form>
      </AuthShell>
    );
  }

  if (step === "recovery") {
    return (
      <AuthShell title="Recovery code" subtitle={showEmailSubtitle ? email : undefined}>
        <form className="auth-form" onSubmit={(event) => void handleRecovery(event)}>
          <label className="field-label auth-field">
            Recovery code
            <input
              className="field-input auth-input"
              value={recoveryCode}
              onChange={(event) => setRecoveryCode(event.target.value)}
              required
            />
          </label>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <AuthSubmitButton label="Verify" loadingLabel="Verifying…" isSubmitting={isSubmitting} />
          <button type="button" className="text-button auth-link" onClick={() => setStep("totp_verify")}>
            Back to authenticator code
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Save your recovery codes"
      lead="Store these codes somewhere safe. Each can be used once."
    >
      <ul className="auth-recovery-list">
        {recoveryCodes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ul>
      <label className="auth-recovery-ack">
        <input
          type="checkbox"
          checked={savedRecoveryAck}
          onChange={(event) => setSavedRecoveryAck(event.target.checked)}
        />
        I saved my recovery codes
      </label>
      <button
        className="primary-button auth-submit"
        type="button"
        disabled={!savedRecoveryAck}
        onClick={() => navigate(routeAfterLogin(enrolledUser, location.state), { replace: true })}
      >
        Continue to FSWL
      </button>
    </AuthShell>
  );
}
